<template>
  <Dialog :visible="true" modal header="Ask students for details" :style="{ width: '46rem' }"
    :breakpoints="{ '640px': '96vw' }" :draggable="false" @update:visible="$emit('close')">
    <!-- Numbered steps: the Stepper shows each step's value in its badge. -->
    <Stepper :value="stepIndex + 1" linear data-testid="data-request-wizard">
      <StepList>
        <Step v-for="(s, i) in WIZARD_STEPS" :key="s.value" :value="i + 1">
          <span :class="{ 'hidden sm:inline': stepIndex !== i }">{{ s.label }}</span>
        </Step>
      </StepList>
      <StepPanels>
        <StepPanel :value="1">
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1.5">
              <label for="request-title" class="text-sm font-medium">Title</label>
              <InputText id="request-title" v-model="draft.title" maxlength="150" :invalid="!!errors['input.title']"
                placeholder="Update your records for the new session" data-testid="request-title" />
              <small v-if="errors['input.title']" class="text-red-500" data-testid="error-title">{{ errors['input.title'] }}</small>
            </div>

            <div class="flex flex-col gap-1.5">
              <label for="request-message" class="text-sm font-medium">Message (optional)</label>
              <Textarea id="request-message" v-model="draft.message" rows="3" autoResize maxlength="2000"
                :invalid="!!errors['input.message']" placeholder="Why you need these details"
                data-testid="request-message" />
              <small v-if="errors['input.message']" class="text-red-500">{{ errors['input.message'] }}</small>
            </div>

            <StudentDataRequestFieldPicker v-model="draft.fields" :preselect="initialFields" :error="errors['input.fields']" />
          </div>
        </StepPanel>

        <StepPanel :value="2">
          <StudentDataRequestAudiencePicker v-model="draft" :count="audienceCount" :counting="countingAudience"
            :error="errors['input.audience']" />
        </StepPanel>

        <StepPanel :value="3">
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1.5">
              <label for="request-due" class="text-sm font-medium">Due date</label>
              <DatePicker inputId="request-due" v-model="draft.dueOn" :minDate="today" showIcon iconDisplay="input"
                dateFormat="dd/mm/yy" :invalid="!!errors['input.due_on']" class="w-full sm:w-64" data-testid="request-due" />
              <small v-if="errors['input.due_on']" class="text-red-500" data-testid="error-due">{{ errors['input.due_on'] }}</small>
              <small v-else class="text-surface-500">Reminders go out 3 days before and on the day. You get a summary the day after.</small>
            </div>

            <div class="flex flex-col gap-3 rounded-lg bg-primary/5 p-4">
              <div class="flex items-start gap-2.5">
                <Checkbox v-model="draft.lockOnClose" inputId="lock-on-close" binary data-testid="request-lock" />
                <label for="lock-on-close" class="cursor-pointer">
                  <span class="text-sm font-medium">Lock the answers when you close the request</span>
                  <span class="mt-0.5 block text-xs text-surface-500">
                    Students can change what they entered until then. After that they ask the school.
                  </span>
                </label>
              </div>
              <div v-if="!isCbtOnlyStudentPortal" class="flex items-start gap-2.5">
                <Checkbox v-model="draft.blocksPortal" inputId="blocks-portal" binary data-testid="request-blocks" />
                <label for="blocks-portal" class="cursor-pointer">
                  <span class="text-sm font-medium">Ask students to answer before using the portal</span>
                  <span class="mt-0.5 block text-xs text-surface-500">
                    Shows a prompt over the student portal until they have filled these in.
                  </span>
                </label>
              </div>
              <small v-if="errors['input.blocks_portal']" class="text-red-500" data-testid="error-blocks">{{ errors['input.blocks_portal'] }}</small>
            </div>
          </div>
        </StepPanel>

        <StepPanel :value="4">
          <div class="flex flex-col gap-4" data-testid="request-preview">
            <p class="text-sm text-surface-600">This is what students will see:</p>
            <StudentDataRequestPreview :title="draft.title" :message="draft.message" :dueLabel="`By ${dueOnText}`"
              :fields="draft.fields" />
            <dl class="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <div><dt class="text-surface-500">Sent to</dt><dd class="font-medium" data-testid="preview-audience">{{ audienceSummary }}</dd></div>
              <div><dt class="text-surface-500">Due</dt><dd class="font-medium">{{ dueOnText }}</dd></div>
              <div><dt class="text-surface-500">When you close it</dt><dd class="font-medium">{{ draft.lockOnClose ? 'Answers are locked' : 'Answers stay editable' }}</dd></div>
              <div><dt class="text-surface-500">Portal</dt><dd class="font-medium">{{ draft.blocksPortal ? 'Prompt until answered' : 'No prompt' }}</dd></div>
            </dl>
          </div>
        </StepPanel>
      </StepPanels>
    </Stepper>

    <Message v-if="generalError" severity="error" :closable="false" class="mt-3" data-testid="wizard-error">{{ generalError }}</Message>

    <template #footer>
      <div class="flex w-full items-center justify-between gap-2">
        <Button v-if="stepIndex > 0" label="Back" severity="secondary" text icon="pi pi-arrow-left" data-testid="wizard-back" @click="back" />
        <span v-else />
        <div class="flex gap-2">
          <Button label="Cancel" severity="secondary" text @click="$emit('close')" />
          <Button v-if="!isLastStep" label="Next" icon="pi pi-arrow-right" iconPos="right" data-testid="wizard-next" @click="next" />
          <Button v-else label="Send request" icon="pi pi-send" :loading="sending" data-testid="wizard-send" @click="onSend" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { useUserStore } from '@/store/userStore';
import StudentDataRequestAudiencePicker from '@/components/DashBoard/StudentDataRequests/StudentDataRequestAudiencePicker.vue';
import StudentDataRequestFieldPicker from '@/components/DashBoard/StudentDataRequests/StudentDataRequestFieldPicker.vue';
import StudentDataRequestPreview from '@/components/StudentDashboard/DataRequests/StudentDataRequestPreview.vue';
import { useStudentDataRequestWizard } from '@/composables/forms/useStudentDataRequestWizard';
import {
  WIZARD_STEPS, audienceSummaryText, formatDay, localDay, type DataRequest,
} from '@/utils/studentDataRequests';

/**
 * The "Ask students for details" wizard: what, who, when, then a check of
 * what students will see. initialFields are ticked to start with (a field's
 * "Ask students").
 */
defineProps<{ initialFields?: ReadonlyArray<string> }>();
const emit = defineEmits<{ close: []; sent: [request: DataRequest] }>();

const toast = useToast();
/** A CBT-only portal shows students exams only: they could not reach the form a prompt sends them to. */
const { isCbtOnlyStudentPortal } = storeToRefs(useUserStore());
const {
  draft, stepIndex, isLastStep, errors, generalError, audienceCount, countingAudience, sending, next, back, send,
} = useStudentDataRequestWizard();

const today = new Date(new Date().setHours(0, 0, 0, 0));

const dueOnText = computed(() => (draft.value.dueOn ? formatDay(localDay(draft.value.dueOn)) : ''));
const audienceSummary = computed(() => audienceSummaryText(draft.value.allStudents, audienceCount.value));

async function onSend() {
  const request = await send();
  if (!request) return;
  toast.add({ severity: 'success', summary: 'Request sent', detail: `${request.recipients_count} students were asked.`, life: 4000 });
  emit('sent', request);
}
</script>
